/**
 * TwoFactorSettings - sezione 2FA nel profilo (Fase 3).
 *
 * Enrollment: setup (QR + secret) -> verify (codice) -> mostra i recovery codes
 * UNA sola volta. Disattivazione: richiede la password.
 * Endpoint: /api/v1/utenti/me/2fa[/setup|/verify|/disable].
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', fontSize: 14, borderRadius: 6,
  border: '1px solid var(--color-border)', backgroundColor: 'var(--color-background)',
  color: 'var(--color-foreground)',
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4, color: 'var(--color-foreground)',
};
const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 24,
};
const btnPrimary: React.CSSProperties = {
  padding: '8px 20px', fontSize: 14, fontWeight: 500, borderRadius: 6,
  backgroundColor: '#1B3A6B', color: '#FFFFFF', border: 'none', cursor: 'pointer',
};
const btnGhost: React.CSSProperties = {
  padding: '8px 16px', fontSize: 13, borderRadius: 6, border: '1px solid var(--color-border)',
  backgroundColor: 'var(--color-background)', color: 'var(--color-foreground)', cursor: 'pointer',
};

type Phase = 'idle' | 'setup' | 'recovery' | 'disable';

const TwoFactorSettings: React.FC = () => {
  const { t } = useTranslation();
  const token = localStorage.getItem('token') ?? '';
  const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [qr, setQr] = useState<string | null>(null);
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [pwd, setPwd] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/v1/utenti/me/2fa', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then((d) => setEnabled(Boolean(d.enabled)))
      .catch(() => setEnabled(false));
  }, [token]);

  const startSetup = async () => {
    setErr(null); setBusy(true);
    try {
      const r = await fetch('/api/v1/utenti/me/2fa/setup', { method: 'POST', headers: authHeaders });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `Errore ${r.status}`);
      const d = await r.json();
      setQr(d.qr_data_uri); setSecret(d.secret); setCode(''); setPwd(''); setPhase('setup');
    } catch (e) { setErr(e instanceof Error ? e.message : 'Errore'); } finally { setBusy(false); }
  };

  const confirmSetup = async () => {
    setErr(null); setBusy(true);
    try {
      const r = await fetch('/api/v1/utenti/me/2fa/verify', {
        method: 'POST', headers: authHeaders, body: JSON.stringify({ codice: code.trim(), password: pwd }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `Errore ${r.status}`);
      const d = await r.json();
      setRecoveryCodes(d.recovery_codes || []); setPhase('recovery'); setEnabled(true);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Errore'); } finally { setBusy(false); }
  };

  const doDisable = async () => {
    setErr(null); setBusy(true);
    try {
      const r = await fetch('/api/v1/utenti/me/2fa/disable', {
        method: 'POST', headers: authHeaders, body: JSON.stringify({ password: pwd }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `Errore ${r.status}`);
      setEnabled(false); setPhase('idle'); setPwd('');
    } catch (e) { setErr(e instanceof Error ? e.message : 'Errore'); } finally { setBusy(false); }
  };

  if (enabled === null) return null;

  return (
    <div style={{ ...cardStyle, marginTop: 24 }}>
      <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-foreground)', marginBottom: 8 }}>
        {t('profile.twofa.title')}
      </h2>
      <p style={{ fontSize: 13, color: enabled ? 'var(--color-accent, #C9952A)' : 'var(--color-muted-foreground)', marginBottom: 16 }}>
        {enabled ? t('profile.twofa.statusOn') : t('profile.twofa.statusOff')}
      </p>

      {err && (
        <p style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, fontSize: 13, backgroundColor: 'rgba(220,38,38,0.1)', color: 'var(--color-destructive, #DC2626)' }}>{err}</p>
      )}

      {phase === 'idle' && !enabled && (
        <button type="button" style={btnPrimary} disabled={busy} onClick={startSetup}>{t('profile.twofa.activate')}</button>
      )}

      {phase === 'idle' && enabled && (
        <button type="button" style={btnGhost} disabled={busy} onClick={() => { setErr(null); setPhase('disable'); }}>{t('profile.twofa.deactivate')}</button>
      )}

      {phase === 'setup' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ fontSize: 13, color: 'var(--color-muted-foreground)' }}>{t('profile.twofa.scan')}</p>
          {qr && <img src={qr} alt="QR 2FA" style={{ width: 180, height: 180 }} />}
          <div>
            <label style={labelStyle}>{t('profile.twofa.secret')}</label>
            <input style={{ ...inputStyle, fontFamily: 'monospace' }} readOnly value={secret} />
          </div>
          <div>
            <label style={labelStyle}>{t('profile.twofa.code')}</label>
            <input style={inputStyle} inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} autoFocus />
          </div>
          <div>
            <label style={labelStyle}>{t('profile.twofa.passwordToConfirm')}</label>
            <input style={inputStyle} type="password" autoComplete="current-password" value={pwd} onChange={(e) => setPwd(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" style={btnPrimary} disabled={busy || !code.trim() || !pwd} onClick={confirmSetup}>{t('profile.twofa.confirm')}</button>
            <button type="button" style={btnGhost} disabled={busy} onClick={() => { setPhase('idle'); setPwd(''); setErr(null); }}>{t('profile.twofa.cancel')}</button>
          </div>
        </div>
      )}

      {phase === 'recovery' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600 }}>{t('profile.twofa.recoveryTitle')}</h3>
          <p style={{ fontSize: 13, color: 'var(--color-destructive, #DC2626)' }}>{t('profile.twofa.recoveryHint')}</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontFamily: 'monospace', fontSize: 14 }}>
            {recoveryCodes.map((c) => (<div key={c} style={{ padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6 }}>{c}</div>))}
          </div>
          <button type="button" style={btnPrimary} onClick={() => { setPhase('idle'); setRecoveryCodes([]); setSecret(''); setQr(null); }}>{t('profile.twofa.done')}</button>
        </div>
      )}

      {phase === 'disable' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={labelStyle}>{t('profile.twofa.passwordToDisable')}</label>
            <input style={inputStyle} type="password" autoComplete="current-password" value={pwd} onChange={(e) => setPwd(e.target.value)} autoFocus />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" style={btnPrimary} disabled={busy || !pwd} onClick={doDisable}>{t('profile.twofa.deactivate')}</button>
            <button type="button" style={btnGhost} disabled={busy} onClick={() => { setPhase('idle'); setPwd(''); setErr(null); }}>{t('profile.twofa.cancel')}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TwoFactorSettings;
